import { createAsyncThunk } from '@reduxjs/toolkit';

import { AttachingMethod, Attachment } from '../../../types';
import {
  getMaxSize,
  getSupportedFileType,
  LEARN_MORE_ARTICLE_URL,
  LIMIT_ATTACH_IMAGES,
  uploadMedia,
} from '../api/uploadMedia';
import { TRootState } from '../reducers';
import { errorsSlice } from '../reducers/errors';
import { AttachmentErrorType, IAttachmentError } from '../reducers/types';
import { selectAttaches } from '../selectors/attaches';
import { selectHasError } from '../selectors/errors';

export const loadAttaches = createAsyncThunk<
  Attachment[],
  { files: File[]; attachingMethod: AttachingMethod; attachFile: boolean; isLiveChat: boolean },
  { state: TRootState; rejectValue: IAttachmentError }
>(
  'loadAttaches',
  async (
    { files: newAttaches, attachingMethod, attachFile, isLiveChat },
    { rejectWithValue, dispatch, getState, signal },
  ) => {
    try {
      const { attachments } = selectAttaches(getState());
      const error = selectHasError(getState());
      const invalidSizeAttaches: File[] = [];
      const invalidTypeAttaches: File[] = [];
      const validAttaches = newAttaches.filter((file: File) => {
        const fileType = getSupportedFileType(file);

        if (fileType === undefined) {
          invalidTypeAttaches.push(file);
          return false;
        } else if (file.size > getMaxSize(fileType)) {
          invalidSizeAttaches.push(file);
          return false;
        }

        return true;
      });

      // reset previous errors when new attachment added
      if (error) {
        dispatch(errorsSlice.actions.clearErrors());
      }

      // if more than allowed files are attached - show error and do not upload new files
      if (attachments.length + newAttaches.length > LIMIT_ATTACH_IMAGES) {
        const lastValidFileIndex = LIMIT_ATTACH_IMAGES - attachments.length;
        const validFiles = newAttaches.slice(0, lastValidFileIndex); // files could be attached because of limit
        const invalidFiles = newAttaches.slice(lastValidFileIndex); // extra files out of limit

        return rejectWithValue({
          localKey: 'attach_error_limit',
          bi: {
            validFiles,
            invalidFiles,
            attachingMethod,
            errorType: AttachmentErrorType.FilesLimit,
          },
        });
      }

      const newFilesCount = newAttaches.length;
      const isScreenshotUpload = !attachFile && !isLiveChat;
      const typeError = getTypeError(
        invalidTypeAttaches,
        validAttaches,
        attachingMethod,
        newFilesCount,
        isScreenshotUpload,
      );
      const sizeError = getSizeError(
        invalidSizeAttaches,
        validAttaches,
        attachingMethod,
        newFilesCount,
        isScreenshotUpload,
      );

      if (typeError) {
        dispatch(errorsSlice.actions.addError(typeError));
      }

      if (sizeError) {
        dispatch(errorsSlice.actions.addError(sizeError));
      }

      if (validAttaches.length === 0) {
        return [];
      }

      // use await before return to handle uploadImage errors in this try/catch
      return await uploadMedia(validAttaches, attachingMethod, signal, attachFile, isLiveChat);
    } catch (err) {
      return rejectWithValue({
        localKey: newAttaches.length === 1 ? 'attach_error_file' : 'attach_error_files',
        bi: {
          validFiles: [],
          invalidFiles: newAttaches,
          attachingMethod,
          errorType: AttachmentErrorType.Network,
        },
      });
    }
  },
);

function getSizeError(
  invalidSizeFiles: File[],
  validFiles: File[],
  attachingMethod: AttachingMethod,
  newFilesCount: number,
  isScreenshotUpload: boolean,
): IAttachmentError | undefined {
  if (invalidSizeFiles.length === 0) {
    // should not show size error if there is no invalid size files
    return;
  }

  const bi: IAttachmentError['bi'] = {
    validFiles,
    attachingMethod,
    invalidFiles: invalidSizeFiles,
    errorType: AttachmentErrorType.FileSize,
  };

  // identify the file type and only return size limit info for the relevant file type.
  // if the error is about multiple file types, then the error will show all 3 options(image, gif and video size), to make it less complicated.
  const relevantSizeTypeError = invalidSizeFiles.every(({ type }) => type === invalidSizeFiles[0].type)
    ? getSupportedFileType(invalidSizeFiles[0])
    : undefined;
  const localKey = isScreenshotUpload
    ? ('screenshot_attach_error_size_file_media' as const)
    : (`attach_error_size_${invalidSizeFiles.length === 1 ? 'file' : 'files'}_${
        relevantSizeTypeError ?? 'media'
      }` as const);

  if (newFilesCount === 1) {
    // if single file is attached just show error message without file names
    return {
      bi,
      localKey,
    };
  } else {
    // if several files is attached show error message and file names(of file/files were not attached because of size validation)
    return {
      bi,
      localKey,
      fileNames: invalidSizeFiles.map((file) => file.name),
    };
  }
}

function getTypeError(
  invalidTypeAttaches: File[],
  validFiles: File[],
  attachingMethod: AttachingMethod,
  newFilesCount: number,
  isScreenshotUpload: boolean,
): IAttachmentError | undefined {
  if (invalidTypeAttaches.length === 0) {
    // should not show type error if there is no invalid type files
    return;
  }

  const bi: IAttachmentError['bi'] = {
    validFiles,
    attachingMethod,
    invalidFiles: invalidTypeAttaches,
    errorType: AttachmentErrorType.FileType,
  };

  const learnMoreLink = {
    localKey: 'attach_error_type_link_text',
    href: LEARN_MORE_ARTICLE_URL,
  } as const;

  if (isScreenshotUpload) {
    return {
      bi,
      localKey: 'screenshot_attach_error_type_file',
      ...(newFilesCount > 1 && { fileNames: invalidTypeAttaches.map((file) => file.name) }),
    };
  }

  // if single file is attached just show error message without file names
  if (newFilesCount === 1) {
    return {
      bi,
      localKey: 'attach_error_type_file',
      learnMoreLink,
    };
  } else {
    return {
      bi,
      localKey: invalidTypeAttaches.length > 1 ? 'attach_error_type_files' : 'attach_error_type_file',
      fileNames: invalidTypeAttaches.map((file) => file.name),
      learnMoreLink,
    };
  }
}
